/**
 * Enhanced Form Field Model
 * Extends GenericFormUICDTO with computed properties and helper methods
 */

import { FormControl, ValidatorFn } from '@angular/forms';
import { GenericFormUICDTO } from 'src/app/Common/UICServices/FormUIC/form-uic.interface';
import { UIInputType, DataFetchType } from '../constants';

/**
 * Enhanced form field interface with computed properties
 */
export interface IFormField extends GenericFormUICDTO {
  // Additional properties not in base DTO
  validationPatternDetailL?: string;
  patchValueFrom?: string;
  actionUser?: string;
  
  // Computed properties for easier access
  readonly isRequired: boolean;
  readonly isSearchable: boolean;
  readonly isVisible: boolean;
  readonly hasValidation: boolean;
  readonly isDependentField: boolean;
  readonly isCalculatedField: boolean;
  readonly isReadOnly: boolean;
  
  // Runtime properties
  control?: FormControl;
  options?: any[];
  loading?: boolean;
  errors?: string[];
  touched?: boolean;
  dirty?: boolean;
}

/**
 * Enhanced form field class with utility methods
 */
export class FormField implements IFormField {
  // GenericFormUICDTO properties (from interface)
  masterType?: string;
  configId!: number;
  formDataType?: string;
  dbDataType?: string;
  dbTableName?: string;
  processSequence?: number;
  companyId?: number;
  branchId?: number;
  dbFeildName!: string;
  uiId?: string;
  uiLabel?: string;
  uiStyle?: string;
  uiClass?: string;
  uiInputType?: string;
  dataFetchType?: string;
  dataFetchFrom!: string;
  defaultValue?: string;
  isUIVisible?: number;
  displaySeq?: number;
  otherDetails?: string;
  wrapperClass?: string;
  customAttribute?: string;
  validationReq?: number;
  validationPattern?: string;
  searchable?: number;
  dependentOn?: string;
  dependentOnKey?: string;
  isActive!: number;
  isDeleted!: number;
  createdBy?: string;
  createdOn?: Date;
  modifiedBy?: string;
  modifiedOn?: Date;
  data: any[] = [];
  isLoading?: boolean;
  dataLoaded?: boolean;
  dependentFields: GenericFormUICDTO[] = [];
  searchTerm?: string;
  displayPropertyName!: string;
  patchPropertyName!: string;
  showDropdown?: boolean;
  patchDataInto!: string;
  
  // Additional properties
  validationPatternDetailL?: string;
  patchValueFrom?: string;
  actionUser?: string;
  
  // Runtime properties
  control?: FormControl;
  options?: any[] = [];
  loading?: boolean = false;
  errors?: string[] = [];
  touched?: boolean = false;
  dirty?: boolean = false;
  
  // ========== COMPUTED PROPERTIES ==========
  
  /**
   * Check if field is required
   */
  get isRequired(): boolean {
    return this.validationReq === 1;
  }
  
  /**
   * Check if field is searchable
   */
  get isSearchable(): boolean {
    return this.searchable === 1 || this.searchable === 2;
  }
  
  /**
   * Check if field allows custom text entry (searchable === 2)
   */
  get allowsCustomText(): boolean {
    return this.searchable === 2;
  }
  
  /**
   * Check if field is visible
   */
  get isVisible(): boolean {
    return this.isUIVisible === 1;
  }
  
  /**
   * Check if field has validation rules
   */
  get hasValidation(): boolean {
    return !!this.validationPatternDetailL || this.validationReq === 1;
  }
  
  /**
   * Check if field depends on another field
   */
  get isDependentField(): boolean {
    return !!this.dependentOn;
  }
  
  /**
   * Check if field is a calculated field (JSExecutor)
   */
  get isCalculatedField(): boolean {
    return this.dataFetchType?.toLowerCase() === DataFetchType.JSExecutor;
  }
  
  /**
   * Check if field is read-only
   */
  get isReadOnly(): boolean {
    return this.customAttribute?.toLowerCase()?.includes('readonly') || false;
  }
  
  /**
   * Check if field requires data fetching
   */
  get requiresDataFetch(): boolean {
    return !!this.dataFetchType && this.dataFetchType !== DataFetchType.None;
  }
  
  /**
   * Check if field is a file upload field
   */
  get isFileUpload(): boolean {
    const fileTypes = ['file', 'image', 'document', 'extdocumentupload'];
    return this.uiInputType ? fileTypes.includes(this.uiInputType.toLowerCase()) : false;
  }
  
  /**
   * Check if field is a select/dropdown field
   */
  get isSelectField(): boolean {
    const selectTypes = ['select', 'selectnadd', 'searchnadd'];
    return this.uiInputType ? selectTypes.includes(this.uiInputType.toLowerCase()) : false;
  }
  
  /**
   * Check if field is a complex/composite field
   */
  get isComplexField(): boolean {
    const complexTypes = ['extaddressdetail', 'extcontactdetail', 'extdocumentupload', 'billingdeliveryaddress'];
    return this.uiInputType ? complexTypes.includes(this.uiInputType.toLowerCase()) : false;
  }
  
  /**
   * Get field type enum value
   */
  get fieldType(): string {
    return this.uiInputType?.toLowerCase() || '';
  }
  
  /**
   * Get data fetch type enum value
   */
  get fetchType(): string {
    return this.dataFetchType?.toLowerCase() || '';
  }
  
  // ========== UTILITY METHODS ==========
  
  /**
   * Factory method to create FormField from DTO
   */
  static fromDTO(dto: GenericFormUICDTO): FormField {
    const field = new FormField();
    Object.assign(field, dto);
    return field;
  }
  
  /**
   * Factory method to create multiple FormFields from DTOs
   */
  static fromDTOs(dtos: GenericFormUICDTO[]): FormField[] {
    return dtos.map(dto => FormField.fromDTO(dto));
  }
  
  /**
   * Convert back to DTO
   */
  toDTO(): GenericFormUICDTO {
    const dto: any = {};
    
    // Copy all DTO properties
    const dtoProps = [
      'configId', 'masterType', 'companyId', 'branchId', 'dbFeildName', 'uiId',
      'uiLabel', 'uiStyle', 'uiClass', 'uiInputType', 'dataFetchType', 'dataFetchFrom',
      'defaultValue', 'isUIVisible', 'displaySeq', 'otherDetails', 'wrapperClass',
      'customAttribute', 'validationReq', 'validationPatternDetailL', 'formDataType',
      'dbDataType', 'dbTableName', 'processSequence', 'searchable', 'dependentOn',
      'patchValueFrom', 'displayPropertyName', 'patchPropertyName', 'patchDataInto',
      'actionUser', 'isActive'
    ];
    
    dtoProps.forEach(prop => {
      if ((this as any)[prop] !== undefined) {
        dto[prop] = (this as any)[prop];
      }
    });
    
    return dto as GenericFormUICDTO;
  }
  
  /**
   * Clone the field
   */
  clone(): FormField {
    return FormField.fromDTO(this.toDTO());
  }
  
  /**
   * Validate field value
   */
  validate(): string[] {
    const errors: string[] = [];
    
    if (!this.control) {
      return errors;
    }
    
    const value = this.control.value;
    
    // Required validation
    if (this.isRequired && !value) {
      errors.push(`${this.uiLabel} is required`);
    }
    
    // Pattern validation
    if (value && this.validationPatternDetailL) {
      // Check against pattern (this would be enhanced with actual pattern matching)
      // For now, just placeholder logic
    }
    
    // Add Angular FormControl errors
    if (this.control.errors) {
      Object.keys(this.control.errors).forEach(errorKey => {
        errors.push(`${this.uiLabel}: ${errorKey}`);
      });
    }
    
    this.errors = errors;
    return errors;
  }
  
  /**
   * Reset field to default state
   */
  reset(): void {
    this.control?.reset(this.defaultValue);
    this.options = [];
    this.loading = false;
    this.errors = [];
    this.touched = false;
    this.dirty = false;
  }
  
  /**
   * Set field value
   */
  setValue(value: any, emitEvent: boolean = true): void {
    this.control?.setValue(value, { emitEvent });
  }
  
  /**
   * Get field value
   */
  getValue(): any {
    return this.control?.value;
  }
  
  /**
   * Mark field as touched
   */
  markAsTouched(): void {
    this.control?.markAsTouched();
    this.touched = true;
  }
  
  /**
   * Check if field has errors
   */
  hasErrors(): boolean {
    return (this.errors?.length || 0) > 0 || (this.control?.invalid && this.control?.touched) || false;
  }
  
  /**
   * Get human-readable field type label
   */
  getFieldTypeLabel(): string {
    const labels: Record<string, string> = {
      'text': 'Text',
      'number': 'Number',
      'email': 'Email',
      'select': 'Dropdown',
      'date': 'Date',
      // ... add more as needed
    };
    return labels[this.fieldType] || this.uiInputType || 'Unknown';
  }
}
